import React, { FC, useState } from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  withStyles,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import * as service from '../../../services/catalog/services';
import ErrorBoundary from '../../../components/ErrorBoundary';
import Loader from '../../../components/loader';
import CircleSuccess from '../../../components/icons/circleSuccess';
import { Link as RouterLink } from 'react-router-dom';
import styled from 'styled-components';
import { OpenInNew } from '@material-ui/icons';

const SuccessLink = styled(RouterLink)`
  text-decoration: underline;
  margin-left: 10px;
`;

const ALTERNATE_TYPES = ['a', 'b', 'c'];

type SkuStatus = 'pending' | 'creating' | 'success' | 'warning' | 'error';

const TextFieldCustom = withStyles({
  root: {
    '& .MuiFilledInput-root': {
      backgroundColor: 'white',
    },
  },
})(TextField);

const CreateProductAlternate: FC = () => {
  const [sku, setSku] = useState('');
  const [selectedAlternates, setSelectedAlternates] = useState<string[]>([]);
  const [existingAlternates, setExistingAlternates] = useState<string[]>([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [confirmDialogSkus, setConfirmDialogSkus] = useState<string[]>([]);
  const [confirmDialogAlternates, setConfirmDialogAlternates] = useState<string[]>([]);
  const [skuStatuses, setSkuStatuses] = useState<Record<string, SkuStatus>>({});
  const [skuMessages, setSkuMessages] = useState<Record<string, string>>({});
  const [baseSku, setBaseSku] = useState<string>('');
  const [productId, setProductId] = useState<number | undefined>(undefined);
  const fetchAlternateStatus = async (skuToCheck: string) => {
    setLoadingStatus(true);
    try {
      const response = await service.getProductAlternateStatus(skuToCheck);
      if (response?.data) {
        if (response.data.error) {
          setErrorMsg(response.data.error);
          setExistingAlternates([]);
        } else {
          setExistingAlternates(response.data.existingAlternates);
          const prefix = skuToCheck.substring(0, 3);
          const restOfSku = skuToCheck.substring(3);
          const matches = restOfSku.match(/([\d]+)([a-zA-Z]+)/);
          if (matches) {
            const numbersInSku = matches[1];
            setBaseSku(`${prefix}${numbersInSku}`);
          } else {
            setBaseSku(skuToCheck);
          }
        }
      }
    } catch (err) {
      setErrorMsg('Error checking alternate status');
      console.error(err);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleSkuChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSku = e.target.value;
    setSku(newSku);
    setSelectedAlternates([]);
    setExistingAlternates([]);
    setErrorMsg('');

    if (newSku.trim()) {
      fetchAlternateStatus(newSku);
    }
  };

  const handleAlternateChange = (alternate: string) => {
    if (existingAlternates.includes(alternate)) {
      return;
    }
    setSelectedAlternates(prev =>
      prev.includes(alternate) ? prev.filter(a => a !== alternate) : [...prev, alternate],
    );
  };

  const handleClose = () => {
    setConfirmDialogSkus([]);
    setConfirmDialogAlternates([]);
    setSkuStatuses({});
    setSkuMessages({});
  };

  const resetForm = () => {
    handleClose();
    setSku('');
    setSelectedAlternates([]);
    setExistingAlternates([]);
    setBaseSku('');
    setErrorMsg('');
    setProductId(undefined);
  };

  const onDialogConfirm = async () => {
    const calculatedBaseSku = baseSku || sku;
    const initialStatuses: Record<string, SkuStatus> = {};
    const initialMessages: Record<string, string> = {};

    confirmDialogSkus.forEach(newSku => {
      initialStatuses[newSku] = 'creating';
      initialMessages[newSku] = '';
    });

    setSkuStatuses(initialStatuses);
    setSkuMessages(initialMessages);

    for (let i = 0; i < confirmDialogAlternates.length; i++) {
      const alternate = confirmDialogAlternates[i];
      const newSku = `${calculatedBaseSku}${alternate}`;

      try {
        const response = await service.createProductAlternate(sku, alternate);
        if (response?.data) {
          setProductId(response.data.product?.id || undefined);
          if (response.data.exportWarnings && response.data.exportWarnings.length > 0) {
            setSkuStatuses(prev => ({ ...prev, [newSku]: 'warning' }));
            setSkuMessages(prev => ({
              ...prev,
              [newSku]: response.data.exportWarnings?.join(', ') || '',
            }));
          } else {
            setSkuStatuses(prev => ({ ...prev, [newSku]: 'success' }));
          }
        }
      } catch (err) {
        const errorMessage =
          err?.response?.data?.message || `Failed to create alternate ${alternate}`;
        setSkuStatuses(prev => ({ ...prev, [newSku]: 'error' }));
        setSkuMessages(prev => ({ ...prev, [newSku]: errorMessage }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');

    if (selectedAlternates.length === 0) {
      setErrorMsg('Please select at least one alternate to create');
      return;
    }

    const calculatedBaseSku = baseSku || sku;
    const newSkus = selectedAlternates.map(alt => `${calculatedBaseSku}${alt}`);
    setConfirmDialogSkus(newSkus);
    setConfirmDialogAlternates(selectedAlternates);
    setSkuStatuses({});
    setSkuMessages({});
  };

  const allComplete =
    confirmDialogSkus.length > 0 &&
    confirmDialogSkus.every(sku => {
      const status = skuStatuses[sku];
      return status === 'success' || status === 'warning' || status === 'error';
    });

  const hasWarnings = Object.values(skuStatuses).some(status => status === 'warning');
  const hasErrors = Object.values(skuStatuses).some(status => status === 'error');
  const isCreating = Object.values(skuStatuses).some(status => status === 'creating');

  return (
    <>
      <form onSubmit={handleSubmit} style={{ width: 600, border: '2px solid black', margin: 10 }}>
        <ErrorBoundary>
          <h4 style={{ textAlign: 'center' }}>Create Product Alternate</h4>
          <div
            style={{
              display: 'flex',
              padding: 20,
              paddingBottom: 0,
              justifyContent: 'space-between',
            }}
          >
            <TextFieldCustom
              variant="filled"
              id="sku"
              label="SKU"
              placeholder="e.g. AHW47010405"
              value={sku}
              onChange={handleSkuChange}
              style={{ width: 275 }}
            />
            <FormGroup style={{ width: 275 }}>
              <div style={{ marginBottom: 8, fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.54)' }}>
                Alternate To Create
              </div>
              {ALTERNATE_TYPES.map(alt => {
                const isExisting = existingAlternates.includes(alt);
                const isSelected = selectedAlternates.includes(alt);
                return (
                  <FormControlLabel
                    key={alt}
                    control={
                      <Checkbox
                        checked={isExisting || isSelected}
                        onChange={() => handleAlternateChange(alt)}
                        disabled={!sku || loadingStatus || isExisting}
                        color="primary"
                      />
                    }
                    label={alt}
                  />
                );
              })}
            </FormGroup>
          </div>
          <div
            style={{
              display: 'flex',
              padding: 20,
              justifyContent: 'space-between',
            }}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={!sku || selectedAlternates.length === 0 || loadingStatus}
            >
              SUBMIT
            </Button>
          </div>
          {errorMsg && (
            <Alert severity="error">
              <AlertTitle>Error</AlertTitle>
              {errorMsg}
            </Alert>
          )}
          <Loader loading={loadingStatus}></Loader>
        </ErrorBoundary>
      </form>
      <Dialog
        open={confirmDialogSkus.length > 0}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">
          Confirm the product alternates you would like to create
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" style={{ marginBottom: 16 }}>
            New SKUs to be created:
          </Typography>
          <List>
            {confirmDialogSkus.map(newSku => {
              const status = skuStatuses[newSku] || 'pending';
              const message = skuMessages[newSku] || '';
              return (
                <ListItem key={newSku}>
                  <ListItemIcon>
                    {status === 'creating' && <CircularProgress size={20} />}
                    {status === 'success' && <CircleSuccess />}
                    {status === 'warning' && <WarningIcon style={{ color: '#ed6c02' }} />}
                    {status === 'error' && <ErrorIcon color="error" />}
                  </ListItemIcon>
                  <ListItemText primary={newSku} secondary={message} />
                </ListItem>
              );
            })}
          </List>
          <Typography variant="h6" style={{ marginTop: 16, marginBottom: 8 }}>
            Copied from SKU:
          </Typography>
          <Typography variant="body1">
            <b>{sku}</b>
            {productId && (
              <>
                <SuccessLink to={`/catalog/products/${productId}`} target="_blank">
                  View Product
                </SuccessLink>
                <OpenInNew fontSize="small" />
              </>
            )}
          </Typography>
          {hasWarnings && (
            <Alert severity="warning" style={{ marginTop: 16 }}>
              <AlertTitle>Warning</AlertTitle>
              Product alternates were created successfully, but some exports may be delayed.
            </Alert>
          )}
          {hasErrors && (
            <Alert severity="error" style={{ marginTop: 16 }}>
              <AlertTitle>Error</AlertTitle>
              Some alternates failed to create. Please check the messages above.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" disabled={isCreating}>
            {allComplete ? 'Close' : 'Cancel'}
          </Button>
          {!allComplete ? (
            <Button onClick={onDialogConfirm} color="primary" disabled={isCreating}>
              {isCreating ? (
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={16} style={{ marginRight: 8 }} />
                  Creating...
                </span>
              ) : (
                'Create Alternates'
              )}
            </Button>
          ) : (
            <Button onClick={resetForm} color="primary" variant="contained">
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateProductAlternate;
